/**
 * Category Data Service
 * Follows data engineer agent principles:
 * - Schema-aware operations
 * - Proper error handling
 * - Performance optimization
 */

export interface CategoryData {
  name: string;
  amount: number;
  percentage: string | number;
  count: number;
  companies?: number;
}

export interface CategoryApiResponse {
  chart_type: string;
  title: string;
  subtitle: string;
  categories: CategoryData[];
  metadata: {
    timeframe: string;
    week_offset: number;
    total_funding: number;
    total_deals: number;
    total_acquisitions?: number; // For M&A category data
    current_period: {
      start: string;
      end: string;
    };
    generated_at: string;
  };
  cache_status?: string;
  response_time_ms?: number;
}

export class CategoryDataService {
  private baseUrl: string;
  private cache = new Map<string, { data: CategoryApiResponse; timestamp: number }>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  constructor(baseUrl?: string) {
    // Use environment variable for API endpoint
    this.baseUrl = baseUrl || import.meta.env.VITE_API_ENDPOINT || 'https://spog-aggregation.mike-532.workers.dev';
  }

  /**
   * Get category data with caching and error handling
   */
  async getCategoryData(timeframe: string = '12w', weekOffset: number = 0, filters?: Record<string, string[] | undefined>): Promise<CategoryApiResponse> {
    // Build filter query string
    const filterParams = filters
      ? Object.entries(filters)
          .filter(([, values]) => values && values.length > 0)
          .map(([key, values]) => `${key}=${values!.join(',')}`)
          .join('&')
      : '';

    const cacheKey = `${timeframe}-${weekOffset}${filterParams ? `-${filterParams}` : ''}`;

    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.data;
    }

    try {
      let url = `${this.baseUrl}/api/v2/category-data?timeframe=${encodeURIComponent(timeframe)}&week_offset=${encodeURIComponent(weekOffset)}`;
      if (filterParams) {
        url += `&${filterParams}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data: CategoryApiResponse = await response.json();

      // Validate response structure
      this.validateApiResponse(data);

      // Cache the response
      this.cache.set(cacheKey, { data, timestamp: Date.now() });

      return data;

    } catch (error) {
      console.error('CategoryDataService: Error fetching data:', error);

      // Return empty state that won't break the UI
      return {
        chart_type: 'horizontal_bar',
        title: 'Top Product Categories by Funding',
        subtitle: 'No data available for this timeframe',
        categories: [],
        metadata: {
          timeframe,
          week_offset: weekOffset,
          total_funding: 0,
          total_deals: 0,
          current_period: {
            start: new Date().toISOString(),
            end: new Date().toISOString()
          },
          generated_at: new Date().toISOString()
        }
      };
    }
  }

  /**
   * Validate API response structure
   * Following data engineer principle: validate before using
   */
  private validateApiResponse(data: any): asserts data is CategoryApiResponse {
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid API response: not an object');
    }

    if (!Array.isArray(data.categories)) {
      throw new Error('Invalid API response: categories must be an array');
    }

    if (data.categories.length > 0) {
      const firstCategory = data.categories[0];
      if (!firstCategory.name || typeof firstCategory.amount !== 'number') {
        throw new Error('Invalid API response: category data structure invalid');
      }
    }

    if (!data.metadata || typeof data.metadata !== 'object') {
      throw new Error('Invalid API response: metadata missing or invalid');
    }
  }

  /**
   * Clear cache (useful for testing or forced refresh)
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Get cache stats for monitoring
   */
  getCacheStats() {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      oldestEntry: Math.min(...Array.from(this.cache.values()).map(entry => entry.timestamp))
    };
  }
}

// Singleton instance
export const categoryDataService = new CategoryDataService();
